import classNames from 'classnames';
import React, { useContext, useLayoutEffect, useRef, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import isChromatic from 'chromatic/isChromatic';
import Avatar from './Avatar';
import styles from './AvatarStack.module.scss';
import Button from './Button';
import Tooltip from './Tooltip';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';

const AVATAR_STACK_USERS_FRAGMENT = gql(`
  fragment AvatarStack_users on User {
    id
    ...Avatar_user
  }
`);

type UsersFragment = DocumentType<typeof AVATAR_STACK_USERS_FRAGMENT>;

const DomRectsContext = React.createContext<(DOMRect | null)[]>([]);

type AvatarStackSpacing = 'tight' | 'loose';
interface AvatarStackContainerProps {
  children: React.ReactNode;
  visibleUsers: UsersFragment[];
  noWrap?: boolean;
  spacing?: AvatarStackSpacing;
  borderSize?: number;
}

export const AvatarStackContainer = ({
  children,
  visibleUsers,
  noWrap,
  spacing,
  borderSize = 3,
}: AvatarStackContainerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [domRects, setDomRects] = useState<(DOMRect | null)[]>([]);

  useLayoutEffect(() => {
    const containerEl = containerRef.current;

    if (containerEl) {
      const resizeObserver = new ResizeObserver(() => {
        setDomRects(
          Array.from(containerEl.children).map((child) =>
            child.getBoundingClientRect(),
          ),
        );
      });
      resizeObserver.observe(containerEl);

      return () => resizeObserver.disconnect();
    }

    return undefined;
  }, [visibleUsers.length]);

  return (
    <DomRectsContext.Provider value={domRects}>
      <div
        ref={containerRef}
        className={classNames(styles.container, {
          [styles.noWrap]: noWrap,
          [styles.tight]: spacing === 'tight',
        })}
        style={
          { '--avatar-border-size': `${borderSize}px` } as React.CSSProperties
        }
        // Due to chromatic render issues
        data-chromatic={isChromatic() ? 'ignore' : undefined}
      >
        {children}
      </div>
    </DomRectsContext.Provider>
  );
};

interface AvatarContainerProps {
  index: number;
  children: React.ReactNode;
}

export const AvatarContainer = ({ index, children }: AvatarContainerProps) => {
  const domRects = useContext(DomRectsContext);
  const rect = domRects[index];
  const nextRect = domRects[index + 1];
  const className = nextRect?.top === rect?.top ? styles.withCutOut : null;

  return <div className={className}>{children}</div>;
};

interface AvatarStackProps {
  users: UsersFragment[];
  showHoverCard?: boolean;
  longHoverDelay?: boolean;
  limit?: number;
  link?: React.ComponentProps<typeof Avatar>['link'];
  size?: number;
  tooltip?: string;
  expandable?: boolean;
  noWrap?: boolean;
  spacing?: 'tight' | 'loose';
  borderSize?: number;
  countOverflowVariant?: 'pill' | 'smallPill' | 'circle';
  renderTooltip?: (
    user: UsersFragment,
  ) => React.ComponentProps<typeof Tooltip>['content'];
}

const AvatarStack = ({
  showHoverCard = false,
  longHoverDelay = false,
  users,
  limit = Infinity,
  link,
  size = 26,
  tooltip,
  expandable,
  noWrap,
  spacing = 'loose',
  borderSize,
  countOverflowVariant = 'pill',
  renderTooltip,
}: AvatarStackProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const shouldSlice = limit < users.length && !isExpanded;
  const visibleUsers = shouldSlice ? users.slice(0, limit - 1) : users;
  const restCount = users.length - visibleUsers.length;
  const restCountPill =
    restCount > 0 ? (
      <div
        className={classNames(
          styles.countContent,
          styles[countOverflowVariant],
        )}
        style={
          countOverflowVariant === 'circle'
            ? { width: size }
            : { fontSize: size / 1.7 }
        }
      >
        {countOverflowVariant === 'circle' ? (
          users.length
        ) : (
          <>
            <span className={styles.plus}>+</span>
            {restCount}
          </>
        )}
      </div>
    ) : null;

  if (users.length === 0) {
    return null;
  }

  return (
    <AvatarStackContainer
      visibleUsers={visibleUsers}
      noWrap={noWrap}
      spacing={spacing}
      borderSize={borderSize}
    >
      {visibleUsers.map((user, i) => (
        <AvatarContainer key={user.id} index={i}>
          <Avatar
            link={link}
            showHoverCard={showHoverCard}
            longHoverDelay={longHoverDelay}
            size={size}
            user={user}
            tooltip={renderTooltip?.(user)}
          />
        </AvatarContainer>
      ))}
      {shouldSlice && (
        <Tooltip content={tooltip} asChild>
          <div>
            {expandable ? (
              <Button
                className={classNames(styles.buttonCount, styles.count)}
                onClick={() => {
                  setIsExpanded(true);
                }}
              >
                {restCountPill}
              </Button>
            ) : (
              <div className={styles.count} style={{ height: size }}>
                {restCountPill}
              </div>
            )}
          </div>
        </Tooltip>
      )}
    </AvatarStackContainer>
  );
};

export default AvatarStack;
